<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useDropdownPosition } from 'dashboard/composables/useDropdownPosition';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { provideDropdownContext } from './provider.js';

const props = defineProps({
  // Render the menu in <body> and pin it to the trigger with fixed coordinates.
  // Use it when an ancestor clips the menu, e.g. a scroll container.
  // Do not use it inside a native <dialog>, the top layer paints above <body>.
  teleport: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);
const [isOpen, toggle] = useToggle(false);

const triggerRef = ref(null);
const floatingRef = ref(null);
const isFloating = computed(() => props.teleport && isOpen.value);

const { fixedPosition, updatePosition } = useDropdownPosition(
  triggerRef,
  floatingRef,
  isFloating,
  { align: 'start' }
);

const closeMenu = () => {
  if (isOpen.value) {
    emit('close');
    toggle(false);
  }
};

provideDropdownContext({
  isOpen,
  toggle,
  closeMenu,
});

// The teleported menu sits outside this container, so a click on an item would
// read as a click outside and close the menu before the item handles it.
const outsideClickHandler = [closeMenu, { ignore: [floatingRef] }];

// The first pass runs before the menu is in the DOM and only sees fallbacks.
watch(isFloating, async floating => {
  if (!floating) return;
  await nextTick();
  updatePosition();
});
</script>

<template>
  <div v-on-click-outside="outsideClickHandler" class="relative space-y-2">
    <template v-if="teleport">
      <span ref="triggerRef" class="inline-flex">
        <slot name="trigger" :is-open :toggle="() => toggle()" />
      </span>
      <TeleportWithDirection to="body">
        <div
          v-if="isOpen"
          ref="floatingRef"
          data-dropdown-teleport
          class="[&>*]:!relative"
          :class="fixedPosition.class"
          :style="fixedPosition.style"
        >
          <slot />
        </div>
      </TeleportWithDirection>
    </template>
    <template v-else>
      <slot name="trigger" :is-open :toggle="() => toggle()" />
      <div v-if="isOpen" class="absolute">
        <slot />
      </div>
    </template>
  </div>
</template>
